/**
 * ErrorBoundary — red de seguridad global.
 *
 * Sin esto, cualquier error de render (un `.map` sobre `undefined`, un chunk
 * que no cargó, una respuesta con forma inesperada) deja al usuario con una
 * pantalla blanca sin salida. Este boundary lo reemplaza por una pantalla con
 * tono de marca y dos caminos: recargar o volver al inicio.
 *
 * - Estilos 100% inline + un <style> mínimo: el error puede ser justamente que
 *   el CSS no cargó, así que la pantalla de error no debe depender de él.
 * - Log solo a `console.error`, sin datos del usuario (la regla de PII del
 *   proyecto): un stack de error no es PII, pero no se agrega nada más.
 * - Es un componente de clase porque `componentDidCatch` /
 *   `getDerivedStateFromError` no tienen equivalente en hooks todavía.
 */
import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Sin PII — solo el error y el árbol de componentes.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleHome = (): void => {
    window.location.href = '/';
  };

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="cc-error-boundary" role="alert">
        <style>{`
          .cc-error-boundary {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
            background: #F7F9FC;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }
          .cc-error-boundary__card {
            max-width: 420px;
            width: 100%;
            background: #FFFFFF;
            border-radius: 16px;
            box-shadow: 0 10px 40px rgba(26, 34, 54, 0.12);
            padding: 40px 32px;
            text-align: center;
          }
          .cc-error-boundary__mark {
            width: 56px;
            height: 56px;
            margin: 0 auto 20px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #EEF4FF;
            color: #1E4A8F;
            font-size: 26px;
            font-weight: 700;
          }
          .cc-error-boundary__title {
            margin: 0 0 8px;
            font-size: 20px;
            font-weight: 700;
            color: #1A2236;
          }
          .cc-error-boundary__text {
            margin: 0 0 24px;
            font-size: 15px;
            line-height: 1.6;
            color: #4A5568;
          }
          .cc-error-boundary__actions {
            display: flex;
            flex-direction: column;
            gap: 10px;
          }
          .cc-error-boundary__btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 44px;
            padding: 0 20px;
            border-radius: 10px;
            font-size: 15px;
            font-weight: 600;
            cursor: pointer;
            border: 1px solid transparent;
            font-family: inherit;
          }
          .cc-error-boundary__btn--primary {
            background: #1E4A8F;
            color: #FFFFFF;
          }
          .cc-error-boundary__btn--ghost {
            background: transparent;
            color: #1E4A8F;
            border-color: #C7D6EC;
          }
          @media (prefers-color-scheme: dark) {
            .cc-error-boundary { background: #1C1C1E; }
            .cc-error-boundary__card { background: #2C2C2E; box-shadow: 0 10px 40px rgba(0,0,0,0.4); }
            .cc-error-boundary__mark { background: rgba(91,155,212,0.16); color: #7AB4E8; }
            .cc-error-boundary__title { color: #F5F5F7; }
            .cc-error-boundary__text { color: rgba(235,235,245,0.75); }
            .cc-error-boundary__btn--primary { background: #5B9BD4; color: #10233B; }
            .cc-error-boundary__btn--ghost { color: #7AB4E8; border-color: rgba(122,180,232,0.4); }
          }
        `}</style>
        <div className="cc-error-boundary__card">
          <div className="cc-error-boundary__mark" aria-hidden="true">!</div>
          <h1 className="cc-error-boundary__title">Algo salió mal</h1>
          <p className="cc-error-boundary__text">
            Tuvimos un problema al mostrar esta página. No es por algo que hayas
            hecho. Probá recargar; si sigue pasando, escribinos.
          </p>
          <div className="cc-error-boundary__actions">
            <button
              type="button"
              className="cc-error-boundary__btn cc-error-boundary__btn--primary"
              onClick={this.handleReload}
            >
              Recargar la página
            </button>
            <button
              type="button"
              className="cc-error-boundary__btn cc-error-boundary__btn--ghost"
              onClick={this.handleHome}
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
